import React, { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'tea-component';
import {
  DEFAULT_NAME,
  DEFAULT_NAME_CN,
  DEFAULT_NAME_EN,
  MAX_FILTERS,
} from '@/pages/datalab/components/UserFilter/FilterPanel/constant';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';
import { useFilterPanalData } from './FilterPanel/hooks';
import { IUserGroup } from '@/pages/datalab/components/UserFilter/FilterPanel/type';
import FilterPanelDropdown from '@/pages/datalab/components/UserFilter/FilterPanelDropdown';
import FilterVsButton from '@/pages/datalab/components/UserFilter/FilterVsButton';
import PlusButton from '@/pages/datalab/components/PlusButton';
import SvgIcon from '@home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';
import './index.scss';

interface IUserFilterProps {
  value?: IUserGroup[];
  gameCode: string;
  label?: string;
  max?: number;
  defaultName?: string;
  defaultNameEn?: string;
  defaultNameCn?: string;
  onChange?: (groups: IUserGroup[]) => void;
}

const UserFilter: React.FC<IUserFilterProps> = ({
  value,
  gameCode,
  label,
  max = MAX_FILTERS,
  defaultName = DEFAULT_NAME,
  defaultNameEn,
  defaultNameCn,
  onChange,
}) => {
  const mergedDefaultNameEn = defaultNameEn || DEFAULT_NAME_EN || defaultName;
  const mergedDefaultNameCn = defaultNameCn || DEFAULT_NAME_CN || defaultName;
  const mergedDefaultName = defaultName || mergedDefaultNameEn || DEFAULT_NAME_CN;

  // 创建空的用户组
  const createEmptyGroup = useCallback(
    (): IUserGroup => ({
      id: `group_${uuidv4()}`,
      name: mergedDefaultName,
      name_en: mergedDefaultNameEn,
      name_cn: mergedDefaultNameCn,
      isNameCustomized: false,
      is_all_users: true,
      filters: [
        {
          id: `filter_${uuidv4()}`,
          field: undefined,
          fieldType: undefined,
          operator: undefined,
          value: undefined,
          hasError: true,
        }
      ],
      logic: 'AND',
      summaryEn: mergedDefaultNameEn,
      summaryCn: mergedDefaultNameCn,
    }),
    [mergedDefaultName, mergedDefaultNameCn, mergedDefaultNameEn]
  );
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { fieldOption, stringLoading, stringDataMap, getStringData } = useFilterPanalData({ gameCode });
  const [groups, setGroups] = useState<IUserGroup[]>(value || [createEmptyGroup()]);

  // 新增用户组，当group变化才触发，防止组件渲染重复创建id不一致导致子组件重新渲染
  const emptyGroupTemplate = useMemo(() => createEmptyGroup(), [groups]);

  // 更新组列表
  const updateGroups = useCallback(
    (updatedGroups: IUserGroup[]) => {
      setGroups(updatedGroups);
      onChange?.(updatedGroups);
    },
    [onChange]
  );

  // 添加新的对照组
  const handleAddGroup = useCallback((updatedGroup: IUserGroup) => {
    // 最多4个对照组
    if (groups.length >= 4) {
      return;
    }
    // 不允许重复添加全部用户，如果没有value值代表全部
    const hasAll = groups?.some(item =>
      item?.filters?.length > 0 && !item.filters[0]?.value
    );
    if (hasAll && updatedGroup?.filters?.length > 0 && !updatedGroup.filters[0]?.value) {
      return notification.warning({
        description: formatMessage({ id: 'dbl_filter_all_users_add' }),
      });
    }
    updateGroups([...groups, updatedGroup]);
  }, [formatMessage, groups, updateGroups]);

  // 更新单个组
  const handleGroupChange = useCallback(
    (groupId: string, updatedGroup: IUserGroup) => {
      const updatedGroups = groups?.map((group) => (group.id === groupId ? updatedGroup : group));
      // 不允许重复添加全部用户，如果没有value值代表全部
      const allList = updatedGroups?.filter(item => !item?.filters?.[0]?.value);
      if (allList?.length > 1) {
        return notification.warning({
          description: formatMessage({ id: 'dbl_filter_all_users_add' }),
        });
      };
      updateGroups(updatedGroups);
    },
    [formatMessage, groups, updateGroups]
  );

  // 确认组编辑
  const handleGroupConfirm = useCallback(
    (groupId: string, confirmedGroup: IUserGroup) => {
      handleGroupChange(groupId, confirmedGroup);
    },
    [handleGroupChange]
  );

  // 删除组
  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      const updatedGroups = groups?.filter((group) => group.id !== groupId);
      if (updatedGroups.length === 0) {
        // 如果删除后没有组，添加一个空的组
        updateGroups([createEmptyGroup()]);
      } else {
        updateGroups(updatedGroups);
      }
    },
    [groups, createEmptyGroup, updateGroups]
  );

  return (
    <div className="dbl-user-filter">
      <span className="dbl-user-filter-label">
        {label || formatMessage({ id: 'dbl_filter_user_filter' })}
      </span>
      <div className="dbl-user-filter-groups">
        {groups?.map((group, index) => (
          <React.Fragment key={group.id}>
            <div className={classNames('dbl-user-filter-group', { 'dbl-user-filter-group-last': index === groups?.length - 1 })}>
              <FilterPanelDropdown
                value={group}
                button={getLanguage(isEn, group?.name_en, group?.name_cn) || group?.name}
                header={formatMessage({ id: 'dbl_filter_user_filter_setting' })}
                fieldOption={fieldOption}
                stringDataMap={stringDataMap}
                stringLoading={stringLoading}
                max={max}
                defaultName={mergedDefaultName}
                defaultNameEn={mergedDefaultNameEn}
                defaultNameCn={mergedDefaultNameCn}
                dropdownProps={
                  {
                    boxClassName: 'dbl-filter-panel-box dbl-filter-panel-box-user',
                    onOpen: () => {
                      const { filters = [] } = group || {};
                      const cache: { [key: string]: boolean; } = {};
                      filters.forEach((filter) => {
                        const { filter_id: filterId = "", fieldType } = filter || {};
                        const hasCache = stringDataMap?.[filterId] || cache[filterId];
                        if (fieldType === 'string' && !hasCache) {
                          cache[filterId] = true;
                          try {
                            getStringData(gameCode, filterId);
                          } catch (error) {
                            console.log(error);
                          }
                        }
                      });
                    }
                  }
                }
                onConfirm={(updatedGroup) => {
                  if (updatedGroup) {
                    handleGroupConfirm(group.id, updatedGroup);
                  }
                }}
                onCancel={() => {
                  // 取消时恢复原值
                }}
                getStringData={(filterId) => {
                  // 无gameCode和id或者缓存中已有数据，不请求接口
                  if (!gameCode || !filterId || stringDataMap?.[filterId]) return;
                  try {
                    getStringData(gameCode, filterId);
                  } catch (error) {
                    console.log(error);
                  }
                }}
              />
              <div
                className={classNames('dbl-user-filter-close')
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveGroup(group.id);
                }}>
                <SvgIcon type="clonse" />
              </div>
            </div>
            {index < groups.length - 1 && <FilterVsButton />}
          </React.Fragment>
        ))}
        <FilterPanelDropdown
          className='dbl-user-filter-groups-add'
          value={emptyGroupTemplate}
          header={formatMessage({ id: 'dbl_filter_user_filter_setting' })}
          fieldOption={fieldOption}
          stringDataMap={stringDataMap}
          max={max}
          defaultName={mergedDefaultName}
          defaultNameEn={mergedDefaultNameEn}
          defaultNameCn={mergedDefaultNameCn}
          dropdownProps={
            {
              boxClassName: 'dbl-filter-panel-box dbl-filter-panel-box-user'
            }
          }
          button={
            <CommonBubble content={groups?.length >= 4 ? formatMessage({ id: 'dbl_filter_add_control_group_disabled' }) : ''}>
              <div>
                <PlusButton
                  type={'weak'}
                  title={formatMessage({ id: 'dbl_filter_add_control_group' })}
                  disabled={groups?.length >= 4}
                />
              </div>
            </CommonBubble>
          }
          onConfirm={(updatedGroup) => {
            if (updatedGroup) {
              handleAddGroup(updatedGroup);
            }
          }}
          onCancel={() => {
            // 取消时恢复原值
          }}
          getStringData={(filterId) => {
            // 无gameCode和id或者缓存中已有数据，不请求接口
            if (!gameCode || !filterId || stringDataMap?.[filterId]) return;
            getStringData(gameCode, filterId);
          }}
        />
      </div>
    </div>
  );
};

export default UserFilter;

